(function () {
  'use strict';

  /*
   * Shared preview magnifier.
   *
   * Current dependents:
   * - sections/cc_configurator.liquid
   * - sections/trio-configurator.liquid
   * - sections/cities-2026-overview.liquid
   * - sections/grand_configurator.liquid
   *
   * Keep modal markup, toolbar IDs/classes, and preview aspect changes in sync
   * across those sections when changing this file.
   */

  /*
   * How close the zoomed view gets. The product masters are 2048px square and
   * the modal already requests them at full size, so this is magnification past
   * native resolution, not extra detail: on a 390px-wide 3x phone the painted
   * image passes 2048px at about 1.75x. Raising it further only softens the
   * image — re-upload the masters larger if the deep zoom needs to stay crisp.
   */
  const ZOOM_LEVEL = 3.5;

  const SELECTORS = {
    modal: '#city-magnifier, #city_preview_modal',
    content: '.city-magnifier_modal-content, .modal-content_city_preview, .modal-content_grand_preview',
    close: '.close_city_magnifier, .close_city_preview',
    zoom: '#city-magnifier-zoom',
    trigger: [
      '.preview_image_img',
      '.gallery-trigger',
      '.cube_scale',
      '.grand_scale',
      '.blok_scale',
      '.grand-configurator-gallery .gallery-trigger'
    ].join(',')
  };

  const state = {
    modal: null,
    stage: null,
    content: null,
    active: null,
    zoom: 1,
    tx: 0,
    ty: 0,
    minTx: 0,
    maxTx: 0,
    minTy: 0,
    maxTy: 0,
    scrollY: 0,
    prevPaddingRight: '',
    locked: false,
    dragId: null,
    startX: 0,
    startY: 0,
    startTx: 0,
    startTy: 0
  };

  function $(selector, root = document) {
    return root.querySelector(selector);
  }

  function normalizeUrl(url) {
    return (url || '').replace(/^\/\//, 'https://');
  }

  function getModal() {
    state.modal = state.modal || $(SELECTORS.modal);
    return state.modal;
  }

  function getZoomButton() {
    return $(SELECTORS.zoom);
  }

  function isMobileViewport() {
    return window.matchMedia('(max-width: 699px)').matches;
  }

  function ensureStage() {
    const modal = getModal();
    if (!modal) return null;

    const content = $(SELECTORS.content, modal);
    if (!content) return null;

    let stage = $('.cf-mag-stage', content);
    if (!stage) {
      stage = document.createElement('div');
      stage.className = 'cf-mag-stage';
      content.appendChild(stage);
    }

    state.stage = stage;
    state.content = content;
    return stage;
  }

  function resetStage() {
    const stage = ensureStage();
    if (!stage) return;

    stage.replaceChildren();
    state.active = null;
    setZoom(1);
  }

  function lockScroll() {
    if (state.locked) return;
    state.locked = true;
    state.scrollY = window.scrollY || 0;
    state.prevPaddingRight = document.body.style.paddingRight;

    const sbw = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.classList.add('modal-open', 'cf-modal-locked');
    document.body.classList.add('modal-open', 'cf-modal-locked', 'magnifier-open');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${state.scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    if (sbw > 0) document.body.style.paddingRight = `${sbw}px`;
  }

  function unlockScroll() {
    if (!state.locked) return;
    state.locked = false;
    document.documentElement.classList.remove('modal-open', 'cf-modal-locked');
    document.body.classList.remove('modal-open', 'cf-modal-locked', 'magnifier-open');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.paddingRight = state.prevPaddingRight || '';
    window.scrollTo(0, state.scrollY || 0);
  }

  function preloadThenSwap(img, highSrc) {
    const high = normalizeUrl(highSrc);
    if (!img || !high || img.src === high) return;

    const loader = new Image();
    loader.decoding = 'async';
    loader.onload = () => {
      if (img.classList.contains('cf-layer')) {
        img.src = high;
        return;
      }

      const overlay = img.cloneNode(false);
      overlay.src = high;
      overlay.classList.add('cf-hi-res-overlay');
      overlay.style.opacity = '0';
      img.after(overlay);
      requestAnimationFrame(() => {
        overlay.style.opacity = '1';
      });
      setTimeout(() => {
        img.src = high;
        overlay.remove();
      }, 320);
    };
    loader.src = high;
  }

  function fittedSize(img, w, h) {
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (!nw || !nh) return null;

    const cover = window.getComputedStyle(img).objectFit === 'cover';
    const fit = cover ? Math.max(w / nw, h / nh) : Math.min(w / nw, h / nh);
    return { w: nw * fit, h: nh * fit };
  }

  /*
   * What the user can actually pan to is the painted image, not the stage: a
   * contained image is letterboxed inside the stage, so stage-sized bounds let
   * it be dragged out into the empty margin instead of stopping at its edge.
   * Anything painted past the stage box is clipped away and unreachable, so
   * clamp to the stage as well. Falls back to the stage until the image loads.
   */
  function visibleSize(w, h) {
    let cw = 0;
    let ch = 0;

    state.active.querySelectorAll('img').forEach((img) => {
      const size = fittedSize(img, w, h);
      if (!size) return;
      cw = Math.max(cw, size.w);
      ch = Math.max(ch, size.h);
    });

    return { w: Math.min(cw || w, w), h: Math.min(ch || h, h) };
  }

  function recalcBounds() {
    if (!state.active || !state.stage) return;
    const w = state.stage.clientWidth;
    const h = state.stage.clientHeight;
    const visible = visibleSize(w, h);
    const extraW = Math.max(0, (visible.w * state.zoom) - w);
    const extraH = Math.max(0, (visible.h * state.zoom) - h);

    state.minTx = -extraW / 2;
    state.maxTx = extraW / 2;
    state.minTy = -extraH / 2;
    state.maxTy = extraH / 2;
    state.tx = Math.min(state.maxTx, Math.max(state.minTx, state.tx));
    state.ty = Math.min(state.maxTy, Math.max(state.minTy, state.ty));
  }

  function applyTransform() {
    if (!state.active) return;
    state.active.style.transformOrigin = 'center center';
    state.active.style.transform = `translate3d(${state.tx}px, ${state.ty}px, 0) scale(${state.zoom})`;
  }

  function setZoom(nextZoom) {
    const modal = getModal();
    state.zoom = nextZoom > 1 ? nextZoom : 1;
    if (state.zoom === 1) {
      state.tx = 0;
      state.ty = 0;
    }

    modal?.classList.toggle('is-zoomed', state.zoom > 1);
    const zoomBtn = getZoomButton();
    zoomBtn?.setAttribute('aria-pressed', state.zoom > 1 ? 'true' : 'false');
    zoomBtn?.setAttribute('data-zoom', String(state.zoom));
    recalcBounds();
    applyTransform();
  }

  function refreshBoundsOnLoad(img) {
    img.addEventListener('load', () => {
      if (!state.active || !state.active.contains(img)) return;
      recalcBounds();
      applyTransform();
    });
  }

  function setInitialZoom() {
    setZoom(isMobileViewport() ? ZOOM_LEVEL : 1);
  }

  function toggleZoom(event) {
    event?.preventDefault();
    setZoom(state.zoom > 1 ? 1 : ZOOM_LEVEL);
  }

  function bindPan(el) {
    if (!el || el.dataset.cfMagnifierBound === 'true') return;
    el.dataset.cfMagnifierBound = 'true';

    el.addEventListener('pointerdown', (event) => {
      if (state.zoom === 1 || !event.isPrimary) return;
      state.dragId = event.pointerId;
      state.startX = event.clientX;
      state.startY = event.clientY;
      state.startTx = state.tx;
      state.startTy = state.ty;
      el.setPointerCapture(state.dragId);
      el.classList.add('is-dragging');
      event.preventDefault();
    }, { passive: false });

    el.addEventListener('pointermove', (event) => {
      if (state.dragId === null || event.pointerId !== state.dragId) return;
      state.tx = Math.min(state.maxTx, Math.max(state.minTx, state.startTx + event.clientX - state.startX));
      state.ty = Math.min(state.maxTy, Math.max(state.minTy, state.startTy + event.clientY - state.startY));
      applyTransform();
      event.preventDefault();
    }, { passive: false });

    const end = (event) => {
      if (state.dragId === null || event.pointerId !== state.dragId) return;
      el.releasePointerCapture(state.dragId);
      el.classList.remove('is-dragging');
      state.dragId = null;
      event.preventDefault();
    };

    el.addEventListener('pointerup', end, { passive: false });
    el.addEventListener('pointercancel', end, { passive: false });
    el.addEventListener('dblclick', toggleZoom, { passive: false });
  }

  function showModal(kind) {
    const modal = getModal();
    if (!modal) return;

    modal.classList.toggle('is-grand-preview', kind === 'grand');
    modal.classList.toggle('is-standard-preview', kind !== 'grand');
    modal.style.display = 'flex';
    modal.classList.add('show');
    lockScroll();

    requestAnimationFrame(() => {
      recalcBounds();
      applyTransform();
    });
  }

  function setPreviewAspect(aspect) {
    const modal = getModal();
    if (!modal) return;

    modal.classList.toggle('is-cube-preview', aspect === 'cube');
    modal.classList.toggle('is-square-preview', aspect !== 'cube');
  }

  function closeModal() {
    const modal = getModal();
    if (!modal) return;

    modal.classList.remove('show', 'is-zoomed', 'is-cube-preview', 'is-square-preview');
    setTimeout(() => {
      modal.style.display = 'none';
      resetStage();
      unlockScroll();
    }, 200);
  }

  function openImage(options) {
    const low = normalizeUrl(options?.preview || options?.low || options?.src || '');
    const high = normalizeUrl(options?.large || options?.high || low);
    if (!low && !high) return;

    const stage = ensureStage();
    if (!stage) return;
    resetStage();
    setPreviewAspect(options?.aspect || 'square');

    const slide = document.createElement('div');
    slide.className = 'cf-mag-slide is-active';
    if (options?.fit === 'cover') slide.classList.add('cf-mag-slide--cover');
    const img = document.createElement('img');
    img.className = 'cf-mag-slide-img';
    if (options?.fit === 'cover') img.classList.add('cf-mag-slide-img--cover');
    img.alt = options?.alt || 'Preview image';
    img.decoding = 'async';
    img.src = low || high;
    refreshBoundsOnLoad(img);

    slide.appendChild(img);
    stage.appendChild(slide);
    state.active = slide;
    bindPan(slide);
    showModal(options?.kind || 'standard');
    setInitialZoom();
    preloadThenSwap(img, high);
  }

  function openLayered(options) {
    const layers = options?.layers || {};
    const stage = ensureStage();
    if (!stage) return;
    resetStage();
    setPreviewAspect('square');

    const slide = document.createElement('div');
    slide.className = 'cf-mag-slide is-active';
    const wrap = document.createElement('div');
    wrap.className = 'grand-preview-wrapper';

    [
      ['bg', 'grand_preview_background_img'],
      ['frame', 'grand_preview_frame_img'],
      ['city', 'grand_preview_image_img'],
      ['front', 'grand_preview_frame_front_img']
    ].forEach(([key, className]) => {
      const src = normalizeUrl(layers[key]);
      if (!src) return;
      const img = document.createElement('img');
      img.className = `${className} cf-layer`;
      img.alt = key === 'city' ? (options.alt || 'GRAND preview') : '';
      img.decoding = 'async';
      img.src = key === 'city' ? normalizeUrl(options.preview || src) : src;
      refreshBoundsOnLoad(img);
      wrap.appendChild(img);
      if (key === 'city') preloadThenSwap(img, normalizeUrl(options.large || src));
    });

    slide.appendChild(wrap);
    stage.appendChild(slide);
    state.active = slide;
    bindPan(slide);
    showModal('grand');
    setInitialZoom();
  }

  function selectedVariant(slotIndex) {
    const select = document.getElementById(`City${slotIndex}`);
    const value = select?.value;
    if (!value) return null;
    return window.CF_VARIANT_LOOKUP?.byId?.[String(value)] || null;
  }

  function openFromTrigger(trigger) {
    if (!trigger) return;

    if (trigger.classList.contains('grand_preview_image_img') && trigger.closest('.grand-gallery-image')) {
      const wrap = trigger.closest('.grand-preview-wrapper');
      openLayered({
        preview: trigger.currentSrc || trigger.src,
        large: trigger.dataset.imgFull || trigger.dataset.largeSrc || trigger.currentSrc || trigger.src,
        alt: trigger.alt,
        layers: {
          bg: $('.grand_preview_background_img', wrap)?.currentSrc || $('.grand_preview_background_img', wrap)?.src,
          frame: $('.grand_preview_frame_img', wrap)?.currentSrc || $('.grand_preview_frame_img', wrap)?.src,
          city: trigger.currentSrc || trigger.src,
          front: $('.grand_preview_frame_front_img', wrap)?.currentSrc || $('.grand_preview_frame_front_img', wrap)?.src
        }
      });
      return;
    }

    let img = trigger.matches('img') ? trigger : $('img[data-large-src], img[data-img-full], .preview_image_img, .cities_cube_image, .cities_grand_image, .cities_blok_image', trigger);
    if (!img) return;

    const wrapper = img.closest('.city-preview-wrapper, .cc_city-preview-wrapper');
    const slotIndex = Number(wrapper?.dataset.cityIndex || img.dataset.cityIndex || 1);
    const variant = selectedVariant(slotIndex);
    const preview = img.dataset.imgPreview || img.dataset.deferredLow || img.currentSrc || img.src || variant?.imgPreview;
    const large = img.dataset.imgFull || img.dataset.largeSrc || img.dataset.full || variant?.imgFull || img.currentSrc || img.src;
    const kind = img.classList.contains('cities_grand_image') ? 'grand' : 'standard';
    const isCubePreview = (
      img.classList.contains('cities_cube_image') ||
      wrapper?.classList.contains('main-variant-slide') ||
      img.closest('.cube_gallery_image_card, .cube-product-gallery-wrapper, .cube-gallery-image') ||
      trigger.closest('.cube_scale, .trio_configurator')
    );
    /*
     * Always contain. Mobile used to cover-fit the CUBE/FRAME previews, but the
     * modal goes full-screen the moment it zooms, and covering that aspect crops
     * the sides of the image off — cropped pixels are clipped, so no amount of
     * panning brings them back.
     */
    openImage({ preview, large, alt: img.alt, kind, fit: 'contain', aspect: isCubePreview ? 'cube' : 'square' });
  }

  function init() {
    const modal = getModal();
    if (!modal) return;

    $(SELECTORS.close, modal)?.addEventListener('click', closeModal);
    getZoomButton()?.addEventListener('click', toggleZoom);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal.classList.contains('show')) closeModal();
    });

    document.body.addEventListener('click', (event) => {
      if (event.target.closest('.select-button, .grand-rotate-btn, .option-button, button, a')) return;
      const trigger = event.target.closest(SELECTORS.trigger);
      if (!trigger) return;
      openFromTrigger(trigger);
    }, { passive: true });

    window.addEventListener('resize', () => {
      recalcBounds();
      applyTransform();
    }, { passive: true });
  }

  window.CFPreviewMagnifier = {
    openImage,
    openLayered,
    close: closeModal,
    setZoom,
    toggleZoom
  };

  window.openCityPreviewModal = function (preview, large) {
    openImage({ preview, large });
  };
  window.openCityMagnifierModal = function (imgElement, startIndex, slotIndex) {
    const img = imgElement || document.querySelector(`.preview_image_img[data-city-index="${slotIndex || 1}"]`);
    openFromTrigger(img);
  };
  window.closeCityMagnifierModal = closeModal;
  window.cfMagToggleZoom = toggleZoom;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
